import React, { useState, useMemo } from 'react';
import { Layout } from './Layout';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from './ui/breadcrumb';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { DateRangeFilter, DateRange } from './DateRangeFilter';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { 
  Home, 
  Shield, 
  Lock, 
  Search, 
  Filter, 
  Eye, 
  Download, 
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { toast } from 'sonner';

// Mock security event data
const generateSecurityEventData = () => {
  const eventTypes = [
    'Failed login attempt',
    'Successful login',
    'Password reset request',
    'Account lockout',
    'Privilege escalation attempt',
    'Unauthorized API access',
    'Data export attempt',
    'Configuration change',
    'Suspicious file access',
    'Multiple device login',
    'Geolocation anomaly',
    'Session hijacking attempt',
    'Brute force attack',
    'SQL injection attempt'
  ];

  const severities = ['Low', 'Medium', 'High', 'Critical'];
  const severityColors = {
    'Low': 'bg-blue-100 text-blue-800 border-blue-200',
    'Medium': 'bg-yellow-100 text-yellow-800 border-yellow-200',
    'High': 'bg-orange-100 text-orange-800 border-orange-200',
    'Critical': 'bg-red-100 text-red-800 border-red-200',
  };

  return Array.from({ length: 500 }, (_, i) => {
    const eventType = eventTypes[Math.floor(Math.random() * eventTypes.length)];
    const severity = severities[Math.floor(Math.random() * severities.length)];
    const timestamp = new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000);
    const isResolved = Math.random() > 0.3;
    
    return {
      id: `sec-${i + 1}`,
      timestamp,
      eventType,
      severity,
      severityColor: severityColors[severity as keyof typeof severityColors],
      description: `${eventType} detected from suspicious IP address`,
      ipAddress: `${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}`,
      isResolved,
      threatScore: Math.floor(Math.random() * 100) + 1,
      affectedUser: `user${Math.floor(Math.random() * 100)}@hospital.com`,
    };
  }).sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
};

export function SecurityAuditLog() {
  const [securityData] = useState(generateSecurityEventData());
  const [selectedEvent, setSelectedEvent] = useState<any>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [dateRange, setDateRange] = useState<DateRange>({
    from: new Date(Date.now() - 24 * 60 * 60 * 1000),
    to: new Date(),
  });
  const [filters, setFilters] = useState({
    severity: 'all',
    status: 'all',
    searchQuery: '',
  });

  const filteredData = useMemo(() => {
    return securityData.filter(event => {
      if (dateRange.from && dateRange.to) {
        if (event.timestamp < dateRange.from || event.timestamp > dateRange.to) {
          return false;
        }
      }
      if (filters.severity !== 'all' && event.severity !== filters.severity) return false;
      if (filters.status !== 'all') {
        if (filters.status === 'resolved' && !event.isResolved) return false;
        if (filters.status === 'unresolved' && event.isResolved) return false;
      }
      if (filters.searchQuery) {
        const query = filters.searchQuery.toLowerCase();
        return event.eventType?.toLowerCase().includes(query) || event.ipAddress?.includes(query);
      }
      return true;
    });
  }, [securityData, dateRange, filters]);

  const summaryStats = useMemo(() => {
    const total = filteredData.length;
    const critical = filteredData.filter(e => e.severity === 'Critical').length;
    const unresolved = filteredData.filter(e => !e.isResolved).length;
    return { total, critical, unresolved };
  }, [filteredData]);

  return (
    <Layout>
      <div className="h-full flex flex-col" style={{ fontSize: '11.2px' }}>
        <div className="p-8 pb-4">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="#" className="flex items-center gap-2">
                  <Home className="w-3 h-3" />
                  Dashboard
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink href="#" className="flex items-center gap-2">
                  <Shield className="w-3 h-3" />
                  Admin Audit
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Security Audit</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>

        <div className="px-8 pb-6">
          <h1 className="text-gray-900 mb-2" style={{ fontSize: '19.6px', fontWeight: 600 }}>
            Security Audit Log
          </h1>
          <p className="text-gray-600" style={{ fontSize: '11.2px' }}>
            Monitor and investigate security events and threats
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Lock className="w-8 h-8 text-blue-500" />
                  <div>
                    <p className="text-gray-900" style={{ fontSize: '18px', fontWeight: 600 }}>
                      {summaryStats.total}
                    </p>
                    <p className="text-gray-600" style={{ fontSize: '10px' }}>Total Events</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="w-8 h-8 text-red-500" />
                  <div>
                    <p className="text-gray-900" style={{ fontSize: '18px', fontWeight: 600 }}>
                      {summaryStats.critical}
                    </p>
                    <p className="text-gray-600" style={{ fontSize: '10px' }}>Critical Events</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <XCircle className="w-8 h-8 text-orange-500" />
                  <div>
                    <p className="text-gray-900" style={{ fontSize: '18px', fontWeight: 600 }}>
                      {summaryStats.unresolved}
                    </p>
                    <p className="text-gray-600" style={{ fontSize: '10px' }}>Unresolved</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        <div className="px-8 pb-6">
          <Card>
            <CardContent className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <DateRangeFilter dateRange={dateRange} onDateRangeChange={setDateRange} />
                <Input
                  placeholder="Search events..."
                  value={filters.searchQuery}
                  onChange={(e) => setFilters(prev => ({ ...prev, searchQuery: e.target.value }))}
                />
                <Select value={filters.severity} onValueChange={(value) => setFilters(prev => ({ ...prev, severity: value }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Severities</SelectItem>
                    <SelectItem value="Low">Low</SelectItem>
                    <SelectItem value="Medium">Medium</SelectItem>
                    <SelectItem value="High">High</SelectItem>
                    <SelectItem value="Critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={filters.status} onValueChange={(value) => setFilters(prev => ({ ...prev, status: value }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                    <SelectItem value="unresolved">Unresolved</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="flex-1 px-8 pb-8">
          <Card>
            <CardHeader>
              <CardTitle style={{ fontSize: '14px' }}>Security Events ({filteredData.length} entries)</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead style={{ fontSize: '11.2px' }}>Timestamp</TableHead>
                    <TableHead style={{ fontSize: '11.2px' }}>Event Type</TableHead>
                    <TableHead style={{ fontSize: '11.2px' }}>Severity</TableHead>
                    <TableHead style={{ fontSize: '11.2px' }}>IP Address</TableHead>
                    <TableHead style={{ fontSize: '11.2px' }}>Status</TableHead>
                    <TableHead style={{ fontSize: '11.2px' }}>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredData.slice(0, 50).map((event) => (
                    <TableRow key={event.id} className={!event.isResolved ? 'bg-red-50' : ''}>
                      <TableCell style={{ fontSize: '11.2px' }}>
                        {event.timestamp.toLocaleString()}
                      </TableCell>
                      <TableCell style={{ fontSize: '11.2px', fontWeight: 500 }}>
                        {event.eventType}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={event.severityColor}>
                          {event.severity}
                        </Badge>
                      </TableCell>
                      <TableCell style={{ fontSize: '11.2px' }}>
                        {event.ipAddress}
                      </TableCell>
                      <TableCell>
                        {event.isResolved ? (
                          <Badge variant="outline" className="bg-green-100 text-green-800 border-green-200">
                            Resolved
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="bg-red-100 text-red-800 border-red-200">
                            Unresolved
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button variant="outline" size="sm" onClick={() => {
                          setSelectedEvent(event);
                          setShowDetails(true);
                        }}>
                          <Eye className="w-3 h-3 mr-1" />
                          Details
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>

        <Dialog open={showDetails} onOpenChange={setShowDetails}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Security Event Details</DialogTitle>
            </DialogHeader>
            {selectedEvent && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                  <div>
                    <p className="text-gray-600" style={{ fontSize: '10px' }}>Event Type</p>
                    <p className="text-gray-900" style={{ fontSize: '11.2px', fontWeight: 500 }}>
                      {selectedEvent.eventType}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-600" style={{ fontSize: '10px' }}>Severity</p>
                    <Badge variant="outline" className={selectedEvent.severityColor}>
                      {selectedEvent.severity}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-gray-600" style={{ fontSize: '10px' }}>IP Address</p>
                    <p className="text-gray-900" style={{ fontSize: '11.2px', fontWeight: 500 }}>
                      {selectedEvent.ipAddress}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-600" style={{ fontSize: '10px' }}>Threat Score</p>
                    <p className="text-gray-900" style={{ fontSize: '11.2px', fontWeight: 500 }}>
                      {selectedEvent.threatScore}/100
                    </p>
                  </div>
                </div>
                <div>
                  <p className="text-gray-600 mb-2" style={{ fontSize: '10px' }}>Description</p>
                  <p style={{ fontSize: '11.2px' }}>{selectedEvent.description}</p>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}